import React from 'react';

import classNames from 'classnames';

import { FxBaseCurrencyNote, FxRate, withIncomplete } from '../../ShowInSelect';

import { columnDefinitions, HeaderCell, MoneyCell, RowLabel, Toggle, totalSuffix } from './byProjectFormat';

import Badge from 'CommonComponents/Badge';

const ROW_TINT = ['', 'tw-bg-charcoal-10', 'tw-text-charcoal-75'];

function FigureCells({ row, locale, committedActuals, footer = false }) {
    return (
        <>
            <MoneyCell value={row.revenue} locale={locale} footer={footer} />
            <MoneyCell value={row.expenditure} locale={locale} footer={footer} />
            <MoneyCell value={row.net} locale={locale} semantic footer={footer} />
            {committedActuals && (
                <>
                    <MoneyCell value={row.committed} locale={locale} footer={footer} />
                    <MoneyCell value={row.actuals} locale={locale} footer={footer} />
                    <MoneyCell value={row.remaining} locale={locale} footer={footer} className="tw-font-semibold" />
                </>
            )}
        </>
    );
}

/** A department or cost centre row, and its children when open. */
function ChildRows({ rows, level, locale, committedActuals, converting, expanded, onToggle }) {
    return rows.map((row) => {
        const expandable = row.children.length > 0;
        const open = expandable && expanded.has(row.key);

        return (
            <React.Fragment key={row.key}>
                <tr className={ROW_TINT[level]}>
                    <td className="tw-text-start">
                        <RowLabel
                            level={level}
                            className={classNames({ 'tw-font-medium': level === 1 })}
                            toggle={expandable ? <Toggle open={open} label={row.name} onClick={() => onToggle(row.key)} /> : null}
                        >
                            {row.name}
                        </RowLabel>
                    </td>
                    {converting && <td />}
                    <FigureCells row={row} locale={locale} committedActuals={committedActuals} />
                    <td />
                </tr>
                {open && (
                    <ChildRows
                        rows={row.children}
                        level={level + 1}
                        locale={locale}
                        committedActuals={committedActuals}
                        converting={converting}
                        expanded={expanded}
                        onToggle={onToggle}
                    />
                )}
            </React.Fragment>
        );
    });
}

function ProjectRows({ project, committedActuals, converting, expanded, onToggle }) {
    const locale = project.locale;
    const expandable = project.children.length > 0;
    const open = expandable && expanded.has(project.key);

    return (
        <>
            <tr>
                <td className="tw-text-start">
                    <RowLabel level={0} toggle={expandable ? <Toggle open={open} label={project.name} onClick={() => onToggle(project.key)} /> : null}>
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
                            <span className="tw-font-semibold">{project.name}</span>
                            {project.currency && <span className="tw-text-xs tw-text-charcoal-50">{project.currency}</span>}
                            <Badge variant="neutral">{project.version}</Badge>
                            <FxBaseCurrencyNote fx={project.fx} />
                        </div>
                    </RowLabel>
                </td>
                {converting && (
                    <td className="inbud-numeric-cell">
                        <FxRate fx={project.fx} />
                    </td>
                )}
                {project.has_activity ? (
                    <FigureCells row={project} locale={locale} committedActuals={committedActuals} />
                ) : (
                    <>
                        <td colSpan={3} className="tw-text-end tw-text-charcoal-50">
                            {$t('Nothing budgeted in this period')}
                        </td>
                        {committedActuals && (
                            <>
                                <MoneyCell value={project.committed} locale={locale} />
                                <MoneyCell value={project.actuals} locale={locale} />
                                <MoneyCell value={project.remaining} locale={locale} className="tw-font-semibold" />
                            </>
                        )}
                    </>
                )}
                <td className="tw-text-end">
                    <a href={project.open_url} className="tw-font-semibold tw-text-blue hover:tw-text-blue-75">
                        {$t('Open')}
                    </a>
                </td>
            </tr>
            {open && (
                <ChildRows
                    rows={project.children}
                    level={1}
                    locale={locale}
                    committedActuals={committedActuals}
                    converting={converting}
                    expanded={expanded}
                    onToggle={onToggle}
                />
            )}
        </>
    );
}

/**
 * Projects as rows: one row per project, opening to its revenue categories
 * and expenditure departments, each opening to cost centres.
 */
export default function ProjectRowsTable({ report, fx, committedActuals, expanded, onToggle }) {
    const definitions = columnDefinitions();
    const converting = !!fx;
    // A project left in its own currency carries its own locale, not the total's.
    const locale = report.mixed_currency ? null : report.projects.find((project) => project.locale && !project.fx?.missing)?.locale || null;

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table">
                <thead>
                    <tr>
                        <HeaderCell definition={definitions.project} numeric={false} />
                        {converting && <HeaderCell definition={definitions.rate} />}
                        <HeaderCell definition={definitions.revenue} />
                        <HeaderCell definition={definitions.expenditure} />
                        <HeaderCell definition={definitions.net} />
                        {committedActuals && (
                            <>
                                <HeaderCell definition={definitions.committed} />
                                <HeaderCell definition={definitions.actuals} />
                                <HeaderCell definition={definitions.remaining} />
                            </>
                        )}
                        <th className="tw-text-end" />
                    </tr>
                </thead>
                <tbody>
                    {report.projects.map((project) => (
                        <ProjectRows
                            key={project.key}
                            project={project}
                            committedActuals={committedActuals}
                            converting={converting}
                            expanded={expanded}
                            onToggle={onToggle}
                        />
                    ))}
                </tbody>
                {report.projects.length > 0 && (
                    <tfoot>
                        <tr>
                            <td className="tw-text-start">
                                {withIncomplete(
                                    <>
                                        {$t('Total')} <span className="tw-font-normal">{totalSuffix(report)}</span>
                                    </>,
                                    fx,
                                )}
                            </td>
                            {converting && <td />}
                            <FigureCells row={report.totals} locale={locale} committedActuals={committedActuals} footer />
                            <td />
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}
